import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Subject, takeUntil } from 'rxjs';
import { BrandService } from 'brand';

export class CustomFieldErrorMatcher implements ErrorStateMatcher {
  constructor(
    private parentControl: AbstractControl,
    private errors: ValidationErrors
  ) {}

  public isErrorState(): boolean {
    const hasErrors = !!this.errors;
    const parentHasErrors = this.parentControl.invalid || hasErrors;
    return this.parentControl && this.parentControl.touched && parentHasErrors;
  }
}

let nextId = 0;

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.has-icon-suffix]': 'clearInputSuffix()',
  },
})
export class FormFieldBaseComponent implements OnInit, OnDestroy, ControlValueAccessor {
  protected _changeDetector = inject(ChangeDetectorRef);
  public ngControl = inject(NgControl, { optional: true, self: true });
  protected brandService = inject(BrandService);
  @Input() public control = new FormControl(this.ngControl?.value);
  @HostBinding('class.custom-form-field')
  public cssClass = true;
  @HostBinding('class.has-icon-suffix')
  public editIcon: string;
  @Input() public class = '';
  @Input() public label = '';
  @Input() public name = '';
  @Input()
  @HostBinding('class.has-icon')
  public icon: string;
  @Input()
  @HostBinding('class.has-icon-suffix')
  public iconSuffix: string;
  @Input()
  @HostBinding('class.has-icon-suffix')
  public checkmarkIcon: string;
  @Input() public placeholder = '';
  @Input() public size = 20;
  @Input() public type = 'text';
  @Input() public labelSuffix: string;
  public showErrors = input(true);
  public subscriptSizing = input<'fixed' | 'dynamic'>();
  public clearInputSuffix = input<'always' | 'never' | 'invalid'>();
  public actionSuffix = input<boolean>(false);
  @Output()
  public inputEvent = new EventEmitter();
  @Output()
  public blurEvent = new EventEmitter();
  @Output()
  public focusEvent = new EventEmitter();
  public actionButtonClick = output<void>();
  @Input()
  @HostBinding('attr.id')
  public id = `form-field-${nextId++}`;

  @Input()
  public get value() {
    return this.control.value;
  }

  public set value(value: string) {
    this.control.patchValue(value);
    this._changeDetector.markForCheck();
  }

  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);

    if (this._disabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this._changeDetector.markForCheck();
  }

  private _disabled = false;
  @Input() public showEditIcon = false;
  private _destroyed = new Subject<void>();

  @Input()
  public get readonly(): boolean {
    return this._readonly;
  }

  public set readonly(value: BooleanInput) {
    this._readonly = coerceBooleanProperty(value);
  }

  private _readonly = false;
  private platformId = inject(PLATFORM_ID);

  public get parentControl() {
    return this.ngControl?.control;
  }

  public get showActionButton() {
    return this.actionSuffix() && !this.ngControl.invalid;
  }

  public get showClearInput() {
    return (
      this.ngControl.enabled &&
      this.ngControl.value &&
      (this.clearInputSuffix() === 'always' ||
        (this.clearInputSuffix() === 'invalid' && this.ngControl.invalid && this.ngControl.touched))
    );
  }

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public ngOnInit() {
    if (this.parentControl) {
      this.checkmarkIcon = this.checkmarkIcon || `check-mark-${this.brandService.brand}`;
      this.editIcon = 'pencil';

      this.control.setValidators(this.parentControl.validator ? this.parentControl.validator : null);
      this.control.updateValueAndValidity();

      this.parentControl.statusChanges.pipe(takeUntil(this._destroyed)).subscribe(() => {
        this._changeDetector.markForCheck();
      });
    }
  }

  public get controlErrorMessage() {
    if (this.parentControl?.errors) {
      return Object.keys(this.parentControl.errors)[0];
    }
  }

  public get controlName(): string | null {
    const formGroup = this.parentControl.parent.controls as {
      [key: string]: AbstractControl;
    };
    return Object.keys(formGroup).find(name => this.parentControl === formGroup[name]) || null;
  }

  public ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  public onChange: (value: unknown) => void = () => undefined;
  public onTouched = (): void => undefined;

  public _onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.onChange(value);
    this.inputEvent.emit(value);
  }

  public _onBlur() {
    if (!this.disabled) {
      this.onTouched();
      this.blurEvent.emit();
      this._changeDetector.markForCheck();
    }
  }

  public _onFocus() {
    if (!this.disabled) {
      this.focusEvent.emit();
      this._changeDetector.markForCheck();
    }
  }

  public writeValue(value: string) {
    this.value = value;
  }

  public registerOnChange(onChange: () => void) {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  public errorMatcher() {
    return this.parentControl && new CustomFieldErrorMatcher(this.parentControl, this.parentControl.errors);
  }

  public get desktopView(): boolean {
    return isPlatformBrowser(this.platformId) ? window.innerWidth > 1023 : true;
  }

  public onClearInput() {
    this.ngControl.reset('');
  }

  public onActionButtonClick() {
    this.actionButtonClick.emit();
  }
}
